import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import addArenaTab from './addArenaTab';
import addCardSlider from './addCardSlider';
import addPostTypes from './addPostTypes';
import addArenaCardEnrichment from './enrichArenaCards';
import addArenaUserExtensions from './addArenaUserExtensions';
import hideCardPlayPosts from './hideCardPlayPosts';
import BattleStartedNotification from './components/BattleStartedNotification';
import BattleJoinedNotification from './components/BattleJoinedNotification';
import BattleEndedNotification from './components/BattleEndedNotification';
import DrawProposedNotification from './components/DrawProposedNotification';
import DrawCancelledNotification from './components/DrawCancelledNotification';
import BattleDeclinedNotification from './components/BattleDeclinedNotification';
import ItemGrantedNotification from './components/ItemGrantedNotification';
import CustomCardBackRequestedNotification from './components/CustomCardBackRequestedNotification';
import LevelUpNotification from './components/LevelUpNotification';
import QuestCompletedNotification from './components/QuestCompletedNotification';
import { BONUS_EFFECT_LABELS, BONUS_EFFECT_COLORS, BONUS_EFFECT_ICONS, ARENA_SETTINGS, ARENA_ICONS, ARENA_CARD_BACKS, setArenaElements, resolveElements } from '../common';
export { default as extend } from './extend';
app.initializers.add('forumaker-arena', () => {
  // ── Populate labels from i18n if translation is available (Weblate-ready) ──
  // safeTrans returns the translated string only if the key was actually found;
  // otherwise returns null so hardcoded defaults in common/index.ts are kept.
  const safeTrans = key => {
    const full = `forumaker-arena.${key}`;
    const result = app.translator.trans(full);
    // Guard: only use if translator returned a real string (not a VNode array, not the key itself)
    return typeof result === 'string' && result !== full ? result : null;
  };

  // Element labels aren't populated here: ELEMENT_LABELS is a live view over
  // the roster (see common/elements.ts), which already falls back to the
  // lib.elements.* keys for any element the admin hasn't renamed.

  const bonusUpdates = {};
  for (const k of Object.keys(BONUS_EFFECT_LABELS)) {
    const v = safeTrans(`lib.bonus_effects.${k}`);
    if (v) bonusUpdates[k] = v;
  }
  if (Object.keys(bonusUpdates).length) Object.assign(BONUS_EFFECT_LABELS, bonusUpdates);
  const mana = safeTrans('lib.stats.mana');
  if (mana) ARENA_SETTINGS.manaLabel = mana;

  // Custom element/bonus-effect colors and icons configured in the admin panel.
  //
  // Inside app.initializers.add, app.forum is ALWAYS still undefined: Flarum's
  // Application.boot() runs all initializers first and only assigns
  // this.forum = this.store.getById(...) afterward. Reading
  // app.forum.attribute(...) directly here would never run, which was the
  // root cause of element/bonus colors and card backs staying at their
  // defaults on every forum load.
  //
  // Moving the read into an override on ForumApplication.prototype.mount
  // fixes it: by the time mount() runs, this.forum is already assigned
  // (boot() guarantees that ordering), and override() lets our code run
  // before the original mount() — i.e. before the first page render, with no
  // extra redraw needed.
  //
  // It must patch ForumApplication.prototype specifically, not the base
  // Application.prototype: ForumApplication overrides mount() itself (calling
  // super.mount() inside), and that own implementation fully shadows the base
  // one in the prototype chain for the real app.mount() call. A direct class
  // import (`flarum/forum/ForumApplication`) doesn't resolve at this
  // extension's build runtime, so the prototype is taken from the live `app`
  // instance instead — that's exactly ForumApplication.prototype.
  override(Object.getPrototypeOf(app), 'mount', function (original) {
    if (app.forum) {
      // The elemental roster arrives already resolved (builtins + legacy
      // color/icon overrides + the admin-configured list) — see
      // Settings\SerializeElements. Running it back through resolveElements()
      // is a no-op for that payload, and keeps the forum on the five builtins
      // if the attribute is missing (e.g. a stale cached payload).
      setArenaElements(resolveElements(app.forum.attribute('arenaElements'), app.forum.attribute('arenaElementColors') || {}, app.forum.attribute('arenaElementIcons') || {}));

      // Stat icons — see ARENA_ICONS. Each falls back to its built-in
      // default if the attribute is missing (stale cached payload).
      ARENA_ICONS.hp = app.forum.attribute('arenaIconsHp') || ARENA_ICONS.hp;
      ARENA_ICONS.mana = app.forum.attribute('arenaIconsMana') || ARENA_ICONS.mana;
      ARENA_ICONS.attack = app.forum.attribute('arenaIconsAttack') || ARENA_ICONS.attack;
      ARENA_ICONS.defense = app.forum.attribute('arenaIconsDefense') || ARENA_ICONS.defense;
      Object.assign(BONUS_EFFECT_LABELS, app.forum.attribute('arenaBonusNames') || {});
      Object.assign(BONUS_EFFECT_COLORS, app.forum.attribute('arenaBonusColors') || {});
      Object.assign(BONUS_EFFECT_ICONS, app.forum.attribute('arenaBonusIcons') || {});

      // Card backs configured in the admin panel. The list is dynamic
      // (arbitrary number of backs, see forumaker\Arena\CardBackManager on the
      // backend) — the server sends it as a single attribute. The array is
      // mutated in place (not reassigned) so every module that imported
      // ARENA_CARD_BACKS sees the updated data through the same reference.
      const cardBacks = app.forum.attribute('arenaCardBacks') || [];
      ARENA_CARD_BACKS.length = 0;
      ARENA_CARD_BACKS.push(...cardBacks);
    }
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return original(...args);
  });
  app.notificationComponents.arenaBattleStarted = BattleStartedNotification;
  app.notificationComponents.arenaBattleJoined = BattleJoinedNotification;
  app.notificationComponents.arenaBattleEnded = BattleEndedNotification;
  app.notificationComponents.arenaDrawProposed = DrawProposedNotification;
  app.notificationComponents.arenaDrawCancelled = DrawCancelledNotification;
  app.notificationComponents.arenaBattleDeclined = BattleDeclinedNotification;
  app.notificationComponents.arenaItemGranted = ItemGrantedNotification;
  app.notificationComponents.arenaCustomCardBackRequested = CustomCardBackRequestedNotification;
  app.notificationComponents.arenaLevelUp = LevelUpNotification;
  app.notificationComponents.arenaQuestCompleted = QuestCompletedNotification;

  // Toggles in the standard notification-settings section
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    items.add('arenaBattleStarted', {
      name: 'arenaBattleStarted',
      icon: 'fas fa-bolt',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_battle_started_label')
    });
    items.add('arenaBattleJoined', {
      name: 'arenaBattleJoined',
      icon: 'fas fa-user-check',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_battle_joined_label')
    });
    items.add('arenaBattleEnded', {
      name: 'arenaBattleEnded',
      icon: 'fas fa-trophy',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_battle_ended_label')
    });
    items.add('arenaDrawProposed', {
      name: 'arenaDrawProposed',
      icon: 'fas fa-handshake',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_draw_proposed_label')
    });
    items.add('arenaDrawCancelled', {
      name: 'arenaDrawCancelled',
      icon: 'fas fa-rotate-left',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_draw_cancelled_label')
    });
    items.add('arenaBattleDeclined', {
      name: 'arenaBattleDeclined',
      icon: 'fas fa-door-closed',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_battle_declined_label')
    });
    items.add('arenaItemGranted', {
      name: 'arenaItemGranted',
      icon: 'fas fa-gift',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_item_granted_label')
    });
    items.add('arenaCustomCardBackRequested', {
      name: 'arenaCustomCardBackRequested',
      icon: 'fas fa-palette',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_custom_card_back_requested_label')
    });
    items.add('arenaLevelUp', {
      name: 'arenaLevelUp',
      icon: 'fas fa-medal',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_level_up_label')
    });
    items.add('arenaQuestCompleted', {
      name: 'arenaQuestCompleted',
      icon: 'fas fa-tasks',
      label: app.translator.trans('forumaker-arena.forum.settings.notify_quest_completed_label')
    });
  });
  addArenaTab();
  addCardSlider();
  addPostTypes();
  addArenaCardEnrichment();
  addArenaUserExtensions();
  hideCardPlayPosts();
});